import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import {
  aTexto,
  mesesTerminados,
  periodoEnPalabras,
  quincenasTerminadas,
  semanasTerminadas,
  type TipoPeriodo,
} from '@renderer/lib/periodosContadora';

/**
 * Sprint 157 — Descargar el reporte de ingresos para la contadora.
 *
 * Diseño decidido con José (2026-09-26):
 *  - Periodo: Mensual (de inicio, el mes pasado), Quincenal, Semanal u Otras fechas, y siempre a
 *    la vista **los días exactos** que va a recibir la contadora.
 *  - Propinas apagadas de inicio: van aparte y no son venta del negocio.
 *  - Sin opción para cambiar cómo se cuenta el ingreso: siempre por el día en que entró el dinero.
 *
 * Cada opción lleva su explicación en una línea: la dueña no tiene por qué saber qué es "quincenal"
 * para efectos de su contadora, pero sí qué días va a mandar.
 */
const OPCIONES: Array<{ tipo: TipoPeriodo; titulo: string; ayuda: string }> = [
  { tipo: 'mensual', titulo: 'Mensual', ayuda: 'Un mes completo. Es lo que casi siempre se manda.' },
  { tipo: 'quincenal', titulo: 'Quincenal', ayuda: 'Del 1 al 15, o del 16 al fin de mes.' },
  { tipo: 'semanal', titulo: 'Semanal', ayuda: 'De lunes a domingo.' },
  { tipo: 'fechas', titulo: 'Otras fechas', ayuda: 'Tú eliges el día de inicio y el de fin.' },
];

function descargar(nombre: string, base64: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  // Se libera después: revocarlo en el mismo tick cancela la descarga en algunos navegadores.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function ReporteContadoraPdf() {
  const hoy = useMemo(() => new Date(), []);
  const meses = useMemo(() => mesesTerminados(hoy), [hoy]);
  const quincenas = useMemo(() => quincenasTerminadas(hoy), [hoy]);
  const semanas = useMemo(() => semanasTerminadas(hoy), [hoy]);

  const [tipo, setTipo] = useState<TipoPeriodo>('mensual');
  const [elegido, setElegido] = useState<Record<Exclude<TipoPeriodo, 'fechas'>, string>>({
    mensual: meses[0].id,
    quincenal: quincenas[0].id,
    semanal: semanas[0].id,
  });
  const [desdeLibre, setDesdeLibre] = useState(meses[0].desde);
  const [hastaLibre, setHastaLibre] = useState(aTexto(hoy));
  const [propinas, setPropinas] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lista = tipo === 'mensual' ? meses : tipo === 'quincenal' ? quincenas : semanas;
  const periodo =
    tipo === 'fechas'
      ? { desde: desdeLibre, hasta: hastaLibre }
      : lista.find((o) => o.id === elegido[tipo]) ?? lista[0];
  const fechasValidas = Boolean(periodo.desde && periodo.hasta && periodo.desde <= periodo.hasta);

  const generar = async () => {
    setGenerando(true);
    setError(null);
    const r = await window.api.contabilidad.pdfIngresos({
      desde: periodo.desde,
      hasta: periodo.hasta,
      incluirPropinas: propinas,
    });
    setGenerando(false);
    if (r.ok) descargar(r.data.nombreArchivo, r.data.pdfBase64);
    else setError(traducirErrorBackend(r.error, r.codigo));
  };

  return (
    <div className="mt-4 space-y-4 border-t border-slate-200 pt-4">
      <h3 className="text-base font-bold text-slate-900">Reporte en PDF para tu contadora</h3>

      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-slate-800">¿Qué periodo le mandas?</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {OPCIONES.map((o) => (
            <label
              key={o.tipo}
              className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border-2 p-3 ${
                tipo === o.tipo ? 'border-primary bg-primary/5' : 'border-slate-300 bg-white'
              }`}
            >
              <input
                type="radio"
                name="periodo-contadora"
                checked={tipo === o.tipo}
                onChange={() => setTipo(o.tipo)}
                className="mt-1 h-5 w-5"
              />
              <span>
                <span className="block text-base font-semibold text-slate-900">{o.titulo}</span>
                <span className="block text-sm text-slate-700">{o.ayuda}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {tipo === 'fechas' ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm font-semibold text-slate-800">
            Desde
            <input
              type="date"
              value={desdeLibre}
              max={aTexto(hoy)}
              onChange={(e) => setDesdeLibre(e.target.value)}
              className="input mt-1 block min-h-[44px]"
            />
          </label>
          <label className="text-sm font-semibold text-slate-800">
            Hasta
            <input
              type="date"
              value={hastaLibre}
              max={aTexto(hoy)}
              onChange={(e) => setHastaLibre(e.target.value)}
              className="input mt-1 block min-h-[44px]"
            />
          </label>
        </div>
      ) : (
        <label className="block text-sm font-semibold text-slate-800">
          {tipo === 'mensual' ? 'Mes' : tipo === 'quincenal' ? 'Quincena' : 'Semana'}
          <select
            value={elegido[tipo]}
            onChange={(e) => setElegido((prev) => ({ ...prev, [tipo]: e.target.value }))}
            className="input mt-1 block min-h-[44px] w-full max-w-md"
          >
            {lista.map((o) => (
              <option key={o.id} value={o.id}>
                {o.etiqueta}
              </option>
            ))}
          </select>
        </label>
      )}

      <p className="rounded-lg bg-slate-100 px-3 py-2 text-base text-slate-900">
        {fechasValidas ? (
          <>
            Tu contadora va a recibir: <b>{periodoEnPalabras(periodo.desde, periodo.hasta)}</b>
          </>
        ) : (
          'La fecha de inicio tiene que ser antes que la de fin.'
        )}
      </p>

      <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={propinas}
          onChange={(e) => setPropinas(e.target.checked)}
          className="mt-1 h-5 w-5"
        />
        <span>
          <span className="block text-base font-semibold text-slate-900">Incluir propinas</span>
          <span className="block text-sm text-slate-700">
            Salen aparte y no se suman a la venta: son de tu personal, no del negocio.
          </span>
        </span>
      </label>

      <p className="text-sm text-slate-700">
        El reporte cuenta cada venta el día en que entró el dinero, igual que tu corte de caja. No
        lleva nombres ni teléfonos de clientes.
      </p>

      <button
        type="button"
        onClick={() => void generar()}
        disabled={generando || !fechasValidas}
        className="btn-primary inline-flex min-h-[48px] items-center gap-2 px-5 text-base"
      >
        <Download className="h-5 w-5" aria-hidden />
        {generando ? 'Preparando el PDF…' : 'Descargar PDF para la contadora'}
      </button>
      {error && (
        <p role="alert" className="text-sm font-medium text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
